import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Search, Plus, ChevronLeft, ChevronRight, Building2, MapPin, Server, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const Empresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ empresa: '', description: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { canCreate, canUpdate, canDelete } = usePermissions('config', 'companies');

  // Sorting
  const [sortField, setSortField] = useState('empresa');
  const [sortOrder, setSortOrder] = useState('asc');
  const sortedEmpresas = [...empresas].sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
      if (search && search.length >= 3) params.append('search', search);
      const response = await api.get(`/config/empresas?${params}`);
      setEmpresas(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) { toast.error('Error al cargar empresas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [pagination.page]);
  useEffect(() => { const t = setTimeout(() => { if (search === '' || search.length >= 3) { setPagination(prev => ({ ...prev, page: 1 })); fetchData(); } }, 800); return () => clearTimeout(t); }, [search]);

  const handleCreate = () => { setEditingItem(null); setFormData({ empresa: '', description: '' }); setModalOpen(true); };
  const handleEdit = (item) => { setEditingItem(item); setFormData({ empresa: item.empresa, description: item.description || '' }); setModalOpen(true); };
  const handleSave = async () => {
    if (!formData.empresa.trim()) { toast.error('El nombre es requerido'); return; }
    try {
      if (editingItem) { await api.put(`/config/empresas/${editingItem.id}`, formData); toast.success('Empresa actualizada'); }
      else { await api.post('/config/empresas', formData); toast.success('Empresa creada'); }
      setModalOpen(false); fetchData();
    } catch (error) { toast.error('Error al guardar'); }
  };
  const handleDelete = async (id) => { try { await api.delete(`/config/empresas/${id}`); toast.success('Empresa eliminada'); setDeleteConfirm(null); fetchData(); } catch (error) { toast.error('Error al eliminar'); } };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Empresas</h1>
            <p className="page-subtitle">Gestión de empresas y clientes</p>
          </div>
          {canCreate && <Button className="btn btn-primary" onClick={handleCreate} data-testid="add-empresa-btn"><Plus className="h-4 w-4" /> Nueva Empresa</Button>}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="stats-card">
            <div className="mb-2"><Building2 className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
            <div className="stats-card-value">{pagination.total}</div>
            <div className="stats-card-label">Empresas activas</div>
          </div>
          <div className="stats-card">
            <div className="mb-2"><MapPin className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
            <div className="stats-card-value">--</div>
            <div className="stats-card-label">Ubicaciones</div>
          </div>
          <div className="stats-card">
            <div className="mb-2"><Server className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
            <div className="stats-card-value">--</div>
            <div className="stats-card-label">Activos</div>
          </div>
        </div>

        {/* Table Card */}
        <Card className="card-subtle overflow-hidden">
          <div className="p-4 border-b border-border/30">
            <h3 className="font-semibold">Empresas activas</h3>
          </div>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div></div>
            ) : (
              <>
                <table className="table-subtle">
                  <thead><tr>
                    <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('empresa')}><div className="flex items-center gap-1.5">Empresa <SortIcon field="empresa" /></div></th>
                    <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('description')}><div className="flex items-center gap-1.5">Descripción <SortIcon field="description" /></div></th>
                    <th>Estado</th>
                    <th className="text-right">Acciones</th>
                  </tr></thead>
                  <tbody>
                    {sortedEmpresas.length === 0 ? (
                      <tr><td colSpan={4}><div className="empty-state"><Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><p className="empty-state-text">No hay empresas</p></div></td></tr>
                    ) : sortedEmpresas.map((item) => (
                      <tr key={item.id}>
                        <td className="font-medium">{item.empresa}</td>
                        <td className="text-muted-foreground">{item.description || '-'}</td>
                        <td><span className="badge badge-success">Activa</span></td>
                        <td className="text-right">
                          <div className="flex justify-end gap-1">
                            {canUpdate && <button onClick={() => handleEdit(item)} className="action-btn action-btn-edit" data-testid={`edit-empresa-${item.id}`}><Edit className="h-3.5 w-3.5" /></button>}
                            {canDelete && <button onClick={() => setDeleteConfirm(item.id)} className="action-btn-delete" data-testid={`delete-empresa-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30">
                  <p className="text-sm text-muted-foreground">{((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}</p>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-8 w-8"><ChevronLeft className="h-4 w-4" /></Button>
                    <span className="text-sm px-2">{pagination.page} / {pagination.pages || 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-8 w-8"><ChevronRight className="h-4 w-4" /></Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Empresa' : 'Nueva Empresa'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body space-y-4">
              <div><Label className="text-sm font-medium">Nombre *</Label><Input value={formData.empresa} onChange={(e) => setFormData({ ...formData, empresa: e.target.value })} className="input-subtle mt-1.5" data-testid="input-empresa" /></div>
              <div><Label className="text-sm font-medium">Descripción</Label><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input-subtle mt-1.5" data-testid="input-description" /></div>
            </div>
            <DialogFooter className="modal-footer"><Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button><Button className="btn btn-primary" onClick={handleSave}>Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="modal-body text-muted-foreground">¿Está seguro que desea eliminar esta empresa?</p>
            <DialogFooter className="modal-footer"><Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button><Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Empresas;
